import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Button, Icon } from "semantic-ui-react";
import BookCardFront from "./BookCardFront";
import ItemSelector from "./ItemSelector";
import BookCardBack from "./BookCardBack";
import {
  extractAvailableItems,
  filterLoanHistoryByItem,
  calculateTotalBookCards,
} from "../utils/bookCardUtils";

function extractStringId(identifier) {
  if (!identifier) return null;
  if (typeof identifier === "string") return identifier;
  if (typeof identifier === "number") return String(identifier);
  if (typeof identifier === "object") {
    return (
      identifier.value || identifier.id || identifier.pid || String(identifier)
    );
  }
  return String(identifier);
}

const STYLES = {
  // Container for the entire component (ItemSelector + Modal)
  mainContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "16px",
    padding: "16px",
  },

  // ItemSelector styles
  selectorContainer: {
    width: "100%",
    maxWidth: "320px",
    padding: "12px",
    backgroundColor: "white",
    borderRadius: "8px",
    border: "2px solid #333",
    marginBottom: "8px",
  },

  // Modal container styles
  modalContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "16px",
    padding: "32px",
    backgroundColor: "rgb(79, 195, 194)",
    borderRadius: "8px",
    maxWidth: "521px",
    maxHeight: "837px",
    margin: "auto auto",
  },

  // Container for book card with side arrows
  bookCardWithArrows: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    position: "relative",
  },

  // Container for title and card info
  titleContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "16px",
    marginBottom: "8px",
    width: "100%",
  },

  title: {
    fontSize: "20px",
    fontWeight: "bold",
    color: "black",
    textAlign: "center",
    marginTop: "-17px",
  },

  cardInfo: {
    fontSize: "19px",
    fontWeight: "bold",
    color: "black",
    textAlign: "center",
    marginTop: "-17px",
  },

  subtitle: {
    fontSize: "14px",
    color: "#666",
    textAlign: "center",
    fontStyle: "italic",
    marginBottom: "16px",
  },

  controlsContainer: {
    display: "flex",
    gap: "10px",
    marginBottom: "16px",
    justifyContent: "center",
    alignItems: "center",
  },

  sideArrowButton: {
    width: "50px",
    height: "50px",
    padding: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(255, 255, 255, 0.9)",
    border: "2px solid #333",
    borderRadius: "50%",
  },

  invisibleArrow: {
    width: "50px",
    height: "50px",
    opacity: 0,
    pointerEvents: "none",
  },
};

const TEXT = {
  MODAL_TITLE: "Book Card",
  SHOW_FRONT: "Show Front",
  SHOW_BACK: "Show Back",
  PRINT_BOOK_CARD: "Print Book Card",
  PRINT_EMPTY_CARD: "Print Empty Card",
};

const FormDataPropType = PropTypes.shape({
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  title: PropTypes.string,
  author: PropTypes.string,
  checkOutDate: PropTypes.string,
  dueDate: PropTypes.string,
  document: PropTypes.object,
  patronName: PropTypes.string,
  hasActiveLoan: PropTypes.bool,
  hasRealData: PropTypes.bool,
});

const DocumentPropType = PropTypes.shape({
  metadata: PropTypes.shape({
    authors: PropTypes.array,
    other_authors: PropTypes.bool,
    title: PropTypes.string,
    call_number: PropTypes.string,
    barcode: PropTypes.string,
    identifiers: PropTypes.array,
    circulation: PropTypes.object,
    items: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  }),
});

const LoanHistoryPropType = PropTypes.arrayOf(
  PropTypes.shape({
    patron: PropTypes.shape({
      first_name: PropTypes.string,
      last_name: PropTypes.string,
      full_name: PropTypes.string,
      year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      program: PropTypes.string,
    }),
    start_date: PropTypes.string,
    end_date: PropTypes.string,
    checkout_date: PropTypes.string,
    due_date: PropTypes.string,
    return_date: PropTypes.string,
    item_pid: PropTypes.string,
    item: PropTypes.object,
  })
);

const BookCardModalWrapper = ({
  formData,
  document,
  loanHistory = [],
  showTitle = true,
  showSubtitle = true,
}) => {
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [showBack, setShowBack] = useState(false);
  const [currentBookCard, setCurrentBookCard] = useState(0);
  const bookCardRef = useRef(null);

  const availableItems = extractAvailableItems(document, loanHistory);

  const filterLoansByItem = (loanHistory, selectedItemId) => {
    if (!selectedItemId) return loanHistory;

    return loanHistory.filter((loan) => {
      // Handle both data structures from Front and Back
      const loanItemId =
        loan.item_pid ||
        loan.item?.pid ||
        loan.metadata?.item_pid?.value ||
        loan.metadata?.item_pid ||
        loan.metadata?.item?.pid;

      // Use your existing extractStringId function
      const actualItemId = extractStringId(loanItemId);
      return actualItemId === selectedItemId;
    });
  };

  // Set default selected item
  useEffect(() => {
    if (availableItems.length > 0 && !selectedItemId) {
      setSelectedItemId(availableItems[0].id);
    }
  }, [availableItems, selectedItemId]);

  // Reset book card when item changes
  useEffect(() => {
    setCurrentBookCard(0);
    setShowBack(false);
  }, [selectedItemId]);

  // Filter loan history for selected item using utility function
  const filteredLoanHistory = filterLoanHistoryByItem(
    loanHistory,
    selectedItemId
  );

  // Calculate total book cards needed using utility function
  const totalBookCards = calculateTotalBookCards(filteredLoanHistory);

  const handleItemChange = (newItemId) => {
    setSelectedItemId(newItemId);
  };

  const handleToggleBookCardSide = () => {
    setShowBack((prevState) => !prevState);
  };

  const handlePreviousCard = () => {
    if (currentBookCard > 0) {
      setCurrentBookCard(currentBookCard - 1);
      setShowBack(false); // Reset to front when navigating
    }
  };

  const handleNextCard = () => {
    if (currentBookCard < totalBookCards - 1) {
      setCurrentBookCard(currentBookCard + 1);
      setShowBack(false); // Reset to front when navigating
    }
  };

  // In BookCardModalWrapper.jsx - Update the handlePrintBookCard function

  const handlePrintBookCard = (isEmpty = false) => {
    if (bookCardRef.current) {
      const printWindow = window.open("", "_blank");
      let bookCardHTML = bookCardRef.current.outerHTML;

      // If printing empty card, replace the content with empty rows
      if (isEmpty) {
        bookCardHTML = generateEmptyBookCardHTML();
      }

      printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Book Card Print</title>
          <style>
            @page {
              size: A4;
              margin: 0.5in;
            }
            body {
              margin: 0;
              padding: 10px;
              font-family: Arial, sans-serif;
              display: flex;
              justify-content: center;
              align-items: flex-start;
            }
            
            /* Print-specific styles */
            @media print {
              body {
                -webkit-print-color-adjust: exact;
                color-adjust: exact;
              }
              
              /* Apply consistent scaling */
              .book-card-container {
                transform: scaleX(0.65) scaleY(0.80) !important;
                transform-origin: top center !important;
              }
              
              /* CRITICAL FIX: Remove height constraint and overflow hidden for print */
              .book-card-container > div {
                height: auto !important;
                min-height: 720px !important;
                overflow: visible !important;
              }
              
              /* Ensure table container doesn't clip */
              .book-card-container table {
                page-break-inside: avoid;
              }
              
              /* Adjust row heights slightly for better fit */
              .book-card-container td {
                height: 26px !important;
                line-height: 1.2 !important;
              }
              
              .book-card-container th {
                height: 22px !important;
                line-height: 1.2 !important;
              }
            }
            
            /* Scale the book card for printing */
            .book-card-container {
              margin: 0 auto;
            }
          </style>
        </head>
        <body>
          <div class="book-card-container">
            ${bookCardHTML}
          </div>
        </body>
      </html>
    `);

      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    }
  };

  // Generate empty book card HTML for printing
  const generateEmptyBookCardHTML = () => {
    if (!bookCardRef.current) return "";

    let originalHTML = bookCardRef.current.outerHTML;

    // Generate empty table rows
    const emptyRows = Array(12)
      .fill(0)
      .map(
        (_, index) => `
      <tr>
        <td style="border: 1px solid #333; padding: 3px; text-align: center; height: 25px;"></td>
        <td style="border: 1px solid #333; padding: 3px; text-align: center; height: 25px;"></td>
        <td style="border: 1px solid #333; padding: 3px; text-align: center; height: 25px;"></td>
        <td style="border: 1px solid #333; padding: 3px; text-align: center; height: 25px;"></td>
      </tr>
    `
      )
      .join("");

    // Replace the table body content with empty rows
    originalHTML = originalHTML.replace(
      /<tbody[^>]*>[\s\S]*?<\/tbody>/gi,
      `<tbody>${emptyRows}</tbody>`
    );

    return originalHTML;
  };

  const renderBookCardContent = () => {
    const filteredLoans = filterLoansByItem(loanHistory, selectedItemId);

    if (showBack) {
      return (
        <BookCardBack
          loanHistory={filteredLoans}
          selectedItemId={selectedItemId}
          currentBookCard={currentBookCard}
        />
      );
    }

    return (
      <BookCardFront
        formData={formData}
        document={document}
        loanHistory={filteredLoans}
        selectedItemId={selectedItemId}
        onItemChange={handleItemChange}
        availableItems={availableItems}
        currentBookCard={currentBookCard}
      />
    );
  };

  const renderLeftArrow = () => {
    if (totalBookCards <= 1) {
      return <div style={STYLES.invisibleArrow}> </div>;
    }

    return (
      <Button
        style={STYLES.sideArrowButton}
        onClick={handlePreviousCard}
        disabled={currentBookCard === 0}
        circular
        icon
        size="large"
      >
        <Icon name="chevron left" size="large" />
      </Button>
    );
  };

  const renderRightArrow = () => {
    if (totalBookCards <= 1) {
      return <div style={STYLES.invisibleArrow}> </div>;
    }

    return (
      <Button
        style={STYLES.sideArrowButton}
        onClick={handleNextCard}
        disabled={currentBookCard === totalBookCards - 1}
        circular
        icon
        size="large"
      >
        <Icon name="chevron right" size="large" />
      </Button>
    );
  };

  return (
    <div style={STYLES.mainContainer}>
      {availableItems.length > 1 && (
        <div style={STYLES.selectorContainer}>
          <ItemSelector
            items={availableItems}
            selectedItemId={selectedItemId}
            onItemChange={handleItemChange}
          />
        </div>
      )}

      <div style={STYLES.modalContainer}>
        {/* Combined title and card info container */}
        {showTitle && (
          <div style={STYLES.titleContainer}>
            <div style={STYLES.title}>{TEXT.MODAL_TITLE}</div>
            {totalBookCards > 1 && (
              <div style={STYLES.cardInfo}>
                {currentBookCard + 1} / {totalBookCards}
              </div>
            )}
          </div>
        )}

        {/* Book card with side arrows */}
        <div style={STYLES.bookCardWithArrows}>
          {renderLeftArrow()}

          <div ref={bookCardRef}>{renderBookCardContent()}</div>

          {renderRightArrow()}
        </div>

        {/* Control buttons */}
        <div style={STYLES.controlsContainer}>
          <Button
            onClick={handleToggleBookCardSide}
            color={showBack ? "blue" : "grey"}
            size="small"
          >
            {showBack ? TEXT.SHOW_FRONT : TEXT.SHOW_BACK}
          </Button>
          <Button
            primary
            onClick={() => handlePrintBookCard(false)}
            icon="print"
            content={TEXT.PRINT_BOOK_CARD}
            size="small"
          />
          <Button
            secondary
            onClick={() => handlePrintBookCard(true)}
            icon="print"
            content={TEXT.PRINT_EMPTY_CARD}
            size="small"
          />
        </div>
      </div>
    </div>
  );
};

BookCardModalWrapper.propTypes = {
  formData: FormDataPropType.isRequired,
  document: DocumentPropType,
  loanHistory: LoanHistoryPropType,
  showTitle: PropTypes.bool,
  showSubtitle: PropTypes.bool,
};

export default BookCardModalWrapper;
